import { useWatch } from 'react-hook-form';
import CURRENCIES from 'v1/helpers/DropdownDataSets/CURRENCIES';
import { formatMoneyAmount } from 'v5/core/money/formatMoney';
import { FormField, FormMessage } from 'v5/design-system/Form';
import { InputGroup, InputGroupButton } from 'v5/design-system/InputGroup';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from 'v5/design-system/Select';
import { cn } from 'v5/platform/dom/cn';
import { Outlet } from 'v5/platform/react/outlet/Outlet';
import { OutletProvider } from 'v5/platform/react/outlet/OutletContext';
import { OutletInject } from 'v5/platform/react/outlet/OutletInject';
import { useFieldContext } from '../FieldContext';
import { fieldAnchor } from '../unitPath';
import { EmptyField, partPlaceholder } from './EmptyField';
import { FieldControlBase } from './FieldControlBase';
import type { FieldComponentProps } from './fieldComponent.types';
import { ghostRowClassName } from './listLayout';
import { NumberGroupInput } from './numberInput';

const CURRENCY_OPTIONS = CURRENCIES.map(currency => ({
  value: currency.code,
  label: currency.code
}));

/**
 * `{ amount: number | null; currency: string }` as one `InputGroup`:
 * currency select + amount input (v5 `createCurrencyControl` group). The
 * amount is in major units (`1,250.50`), as legacy `CurrencyInput` showed it.
 */
export function CurrencyField({
  spec,
  path,
  hideLabel
}: FieldComponentProps<'currency'>) {
  const ctx = useFieldContext();
  const readOnly = ctx.mode === 'display';
  const amount: number | null = useWatch({ name: `${path}.amount` }) ?? null;
  const currency: string = useWatch({ name: `${path}.currency` }) ?? '';

  // A currency alone is no value (the codec's `isEmpty` agrees).
  if (readOnly && amount === null) {
    return <EmptyField spec={spec} path={path} hideLabel={hideLabel} />;
  }

  return (
    <FieldControlBase spec={spec} hideLabel={hideLabel} path={path}>
      <div>
        <OutletProvider>
          <InputGroup
            size={ctx.size}
            variant={ctx.variant}
            className={cn(ctx.ghost && ghostRowClassName)}
            aria-readonly={readOnly}
          >
            <FormField
              name={`${path}.currency`}
              render={({ field }) => (
                <>
                  <Select
                    name={field.name}
                    value={field.value ?? ''}
                    onValueChange={field.onChange}
                  >
                    <InputGroupButton asChild>
                      <SelectTrigger
                        variant="headless"
                        className="w-auto"
                        readOnly={readOnly}
                        ref={field.ref}
                        onBlur={field.onBlur}
                        {...fieldAnchor(field.name)}
                      >
                        <SelectValue
                          placeholder={partPlaceholder(
                            ctx.mode,
                            'Select currency',
                            (field.value ?? '') === ''
                          )}
                        />
                      </SelectTrigger>
                    </InputGroupButton>
                    <SelectContent>
                      {CURRENCY_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <OutletInject>
                    <FormMessage />
                  </OutletInject>
                </>
              )}
            />
            <FormField
              name={`${path}.amount`}
              render={({ field }) => (
                <>
                  <NumberGroupInput
                    readOnly={readOnly}
                    placeholder="Enter amount"
                    format={amount => formatMoneyAmount(amount, 'en')}
                    currency={currency === '' ? undefined : currency}
                    {...field}
                    {...fieldAnchor(field.name)}
                  />
                  <OutletInject>
                    <FormMessage />
                  </OutletInject>
                </>
              )}
            />
          </InputGroup>
          <Outlet className="empty:contents p-2" />
        </OutletProvider>
      </div>
    </FieldControlBase>
  );
}
